import { MessagingRepository } from '@/io/repository/MessagingRepository.ts';
import { CreateMessage } from '@/models/MessagingModel.ts';
import { PractitionerQueries } from '@/queries/PractitionerQueries.ts';
import { makeQueryKey } from '@/queries/Queries';
import { createInfiniteSuspenseQuery } from '@/queries/utils/CreateInfiniteSuspenseQuery.ts';
import { createMutation } from '@/queries/utils/CreateMutation';
import { createSuspenseQuery } from '@/queries/utils/CreateSuspenseQuery.ts';

type MessagesRequest = {
  patientId: string;
  cursor?: string;
};

export class MessagingQueries {
  constructor(
    private readonly practitioners: PractitionerQueries = new PractitionerQueries(),
    private readonly messaging: MessagingRepository = new MessagingRepository(),
  ) {}

  /*******************************/
  /*********** QUERIES ***********/
  /*******************************/

  useMessages = createInfiniteSuspenseQuery(
    'chat',
    ({ patientId }) => makeQueryKey(patientId),
    async ({ patientId, cursor }: MessagesRequest) =>
      await this.messaging.getMessages(patientId, cursor),
    (lastPage, _, lastPageParam) => {
      if (!lastPage.next) {
        return null;
      }
      const url = new URL(lastPage.next);
      const cursor = url.searchParams.get('cursor') ?? undefined;
      if (!cursor) {
        return null;
      }
      return {
        ...lastPageParam,
        cursor,
      };
    },
    {
      getPreviousPageParam: (firstPage, _, firstPageParam) => {
        if (!firstPage.prev) {
          return null;
        }
        const url = new URL(firstPage.prev);
        const cursor = url.searchParams.get('cursor') ?? undefined;
        if (!cursor) {
          return null;
        }
        return {
          ...firstPageParam,
          cursor,
        };
      },
      onSuccess: (_, { patientId }, { queryClient }) => {
        this.useUnreadMessages.invalidate(queryClient, { patientId });
        this.practitioners.usePatientsList.invalidate(queryClient, {});
      },
    },
  );

  useDraftMessage = createSuspenseQuery(
    'use-draft-message',
    ({ practitionerId, patientId }) => makeQueryKey(practitionerId, patientId),
    async (data: { practitionerId: string; patientId: string }) => ({
      message: await this.messaging.getDraftMessage(
        data.practitionerId,
        data.patientId,
      ),
      patientId: data.patientId,
    }),
  );

  useUnreadMessages = createSuspenseQuery(
    'unread-messages',
    ({ patientId }) => makeQueryKey(patientId),
    async (data: { patientId: string }) =>
      this.messaging.getUnreadMessages(data.patientId),
  );

  /*******************************/
  /********** MUTATIONS **********/
  /*******************************/

  /*
  Don't invalidate messages on read.
  Keep unread messages display until the next natural refresh
   */
  useReadMessages = createMutation(
    'read-messages',
    async ({ patientId }: { patientId: string }) =>
      this.messaging.readMessages(patientId),
    {
      onSuccess: (_, { patientId }, { queryClient }) => {
        this.useUnreadMessages.invalidate(queryClient, { patientId });
        this.practitioners.usePatientsList.invalidate(queryClient, {});
      },
    },
  );

  useCreateMessage = createMutation(
    'create-message',
    async ({ patientId, ...data }: { patientId: string } & CreateMessage) =>
      this.messaging.createMessage(patientId, data),
    {
      onSuccess: (_, { patientId }, { queryClient }) => {
        this.useMessages.invalidate(queryClient, {
          patientId,
        });
      },
    },
  );

  useDeleteMessage = createMutation(
    'delete-message',
    async ({
      patientId,
      messageId,
    }: {
      patientId: string;
      messageId: string;
    }) => this.messaging.deleteMessage(patientId, messageId),
    {
      onSuccess: (_, { patientId }, { queryClient }) => {
        this.useMessages.invalidate(queryClient, {
          patientId,
        });
      },
    },
  );

  useUpdateDraftMessage = createMutation(
    'update-draft-message',
    async ({
      practitionerId,
      patientId,
      message,
    }: {
      practitionerId: string;
      patientId: string;
      message?: string;
    }) =>
      message
        ? this.messaging.draftMessage(practitionerId, patientId, message)
        : this.messaging.clearDraftMessage(practitionerId, patientId),
    {
      onSuccess: (_, { practitionerId, patientId }, { queryClient }) => {
        this.useDraftMessage.invalidate(queryClient, {
          practitionerId,
          patientId,
        });
      },
    },
  );
}
